/*
Ticket-price status for the tours posts in the post config form.

A tours ficha feeds Google an event per show, and each event can only carry a
price when at least one of that show's visible tickets has one. Tickets are
stored with price 0 until somebody types the real figure, and nothing in the
manager used to say so — the omission only surfaced in Search Console, weeks
later, as "Missing field offers".

Advisory only: saving stays available. A genuinely free show reads the same as
an unpriced one in this data model, so the wording never accuses.

CommonJS on purpose: ticketPriceStatus.test.js runs under plain `node` without
the webpack/babel toolchain (this package has no test framework); the components
import it through babel's CJS interop.
*/

// price 0 is what the backend stores until an editor sets the real one, so it
// means "not priced yet" rather than "free". Hidden tickets never render on the
// page, so they can neither warn nor rescue a show.
const isUnpricedTicket = (ticket) => {
  if (!ticket || ticket.show === false) return false;
  return !(Number(ticket.price) > 0);
};

const isUnpricedShow = (show) => {
  const tickets = (show && Array.isArray(show.tickets) ? show.tickets : [])
    .filter(ticket => ticket && ticket.show !== false);
  return !tickets.some(ticket => Number(ticket.price) > 0);
};

/*
Whether a ticket is shown on a tours post. postSlugNames comes from the events
join in getTicketsList/getTickets — a ticket document itself has no idea where
it appears. `tourPostSlugs` is the set of slugs in the tours category; before it
has loaded, nothing is a tours ticket and no warning shows.
*/
const isTourTicket = (ticket, tourPostSlugs) => {
  const slugs = ticket && Array.isArray(ticket.postSlugNames) ? ticket.postSlugNames : [];
  if (!slugs.length || !tourPostSlugs) return false;
  const has = tourPostSlugs instanceof Set
    ? slug => tourPostSlugs.has(slug)
    : slug => tourPostSlugs.indexOf(slug) !== -1;
  return slugs.some(has);
};

/*
Whether an unpriced ticket is worth warning about.

A ticket is only classifiable once some event lists it, and roughly a quarter of
them are not listed by any event yet — which is precisely when a price is most
likely to be forgotten, right after the ticket is created. Treating "no event
yet" as a candidate costs very little: unattached tickets are almost never left
unpriced *and* visible, so a handful of extra rows buys back every tours ticket
that would otherwise be flagged only after someone wired it up.

Silence still needs evidence: a ticket attached to a post that is not a tour is
ruled out, and no ticket at all — the create form — never warns, because there
is nothing to be wrong about yet.
*/
const mayNeedTourPrice = (ticket, tourPostSlugs) => {
  if (!ticket) return false;
  const slugs = Array.isArray(ticket.postSlugNames) ? ticket.postSlugNames : [];
  return slugs.length ? isTourTicket(ticket, tourPostSlugs) : true;
};

// Events store beginDateTime as an epoch-millis string. A show we cannot date
// counts as upcoming: silence should need evidence.
const hasPassed = (show, now) => {
  const raw = show && show.beginDateTime;
  if (!raw) return false;
  const value = /^\d+$/.test(String(raw)) ? Number(raw) : Date.parse(raw);
  if (!Number.isFinite(value)) return false;
  return value < now.getTime();
};

const getTourPriceWarnings = (shows, now) => {
  const at = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  // A tour that is over can never be priced — its tickets are gone. Nagging
  // about it would train editors to ignore the whole advisory block.
  const list = (Array.isArray(shows) ? shows : []).filter(show => !hasPassed(show, at));
  if (!list.length) return [];

  const unpriced = list.filter(isUnpricedShow).length;
  if (!unpriced) return [];

  return [`${unpriced} of ${list.length} shows have no ticket price: they cannot show a price in Google.`];
};

module.exports = {
  isUnpricedTicket,
  isUnpricedShow,
  isTourTicket,
  mayNeedTourPrice,
  getTourPriceWarnings,
};
